/**
 *  @class
 *  @function TextCollapse
 *  Klapper lang tekst sammen til en fast hoejde med en "Laes mere"-knap.
 *  Teksten er altid fuldt til stede i HTML'en (SEO); kun visningen klippes.
 */

if (!customElements.get('text-collapse')) {
  class TextCollapse extends HTMLElement {
    constructor() {
      super();
      this.check = this.check.bind(this);
      this.onToggle = this.onToggle.bind(this);
    }

    connectedCallback() {
      this.content = this.querySelector('.text-collapse__content');
      this.toggle = this.querySelector('.text-collapse__toggle');
      if (!this.content || !this.toggle) {
        return;
      }
      this.expanded = false;
      this.toggle.addEventListener('click', this.onToggle);
      window.addEventListener('resize', this.check);
      this.check();
      // Fonte og billeder kan aendre hoejden efter foerste render.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.check);
      }
      window.addEventListener('load', this.check, { once: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.check);
      window.removeEventListener('load', this.check);
    }

    get collapsedHeight() {
      const value = parseInt(getComputedStyle(this).getPropertyValue('--text-collapse-height'), 10);
      return isNaN(value) ? 180 : value;
    }

    /* Vis kun knappen, hvis teksten reelt er laengere end den sammenklappede hoejde. */
    check() {
      if (this.expanded) {
        return;
      }
      const fits = this.content.scrollHeight <= this.collapsedHeight + 40;
      this.classList.toggle('text-collapse--fits', fits);
      this.toggle.hidden = fits;
      if (fits) {
        this.removeAttribute('data-collapsed');
      } else {
        this.setAttribute('data-collapsed', '');
      }
    }

    onToggle() {
      if (this.expanded) {
        this.collapse();
      } else {
        this.expand();
      }
    }

    expand() {
      this.expanded = true;
      this.content.style.maxHeight = this.collapsedHeight + 'px';
      this.removeAttribute('data-collapsed');
      requestAnimationFrame(() => {
        this.content.style.maxHeight = this.content.scrollHeight + 'px';
        const done = () => {
          this.content.style.maxHeight = 'none';
          this.content.removeEventListener('transitionend', done);
        };
        this.content.addEventListener('transitionend', done);
      });
      this.toggle.textContent = this.toggle.dataset.readLess;
      this.toggle.setAttribute('aria-expanded', 'true');
    }

    collapse() {
      this.expanded = false;
      this.content.style.maxHeight = this.content.scrollHeight + 'px';
      // Tving reflow, saa overgangen starter fra den fulde hoejde.
      void this.content.offsetHeight;
      this.content.style.maxHeight = '';
      this.setAttribute('data-collapsed', '');
      this.toggle.textContent = this.toggle.dataset.readMore;
      this.toggle.setAttribute('aria-expanded', 'false');
      // Bring toppen af teksten i syne igen, hvis brugeren har scrollet forbi den.
      const top = this.getBoundingClientRect().top;
      if (top < 0) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        window.scrollBy({ top: top - 100, behavior: behavior });
      }
    }
  }

  customElements.define('text-collapse', TextCollapse);
}
